/** Offline preview only. Never pass this document to the production renderer. */
export const SCHEMA = 'luma-preparation-preview-v1';
export type Span = readonly [number, number]; // Unicode code points, half-open
export interface Source {
  reference: string; version: string; text: string; sha256: string;
  rights: string; mode: 'exact-source';
}
export interface Unit { id: string; span: Span; predecessors: string[] }
export interface Paragraph extends Unit { kind: 'paragraph' | 'lineated' }
export interface Sentence extends Unit { paragraphIds: string[] }
export interface Character { id: string; name: string; aliases: string[] }
export interface Turn extends Unit {
  role: 'narration' | 'dialogue'; characterId: string | null;
  state: 'unknown' | 'suggested' | 'human_confirmed' | 'human_unresolved';
  candidates: string[]; evidence: string;
}
export interface Passage { id: string; sentenceIds: string[]; predecessors: string[] }
export interface Preview {
  schema: string; id: string; previousRevision: string | null; source: Source;
  paragraphs: Paragraph[]; sentences: Sentence[]; turns: Turn[];
  characters: Character[]; passages: Passage[]; boundariesReviewed: boolean;
  warnings: string[];
}
export interface RequestPlan {
  previewOnly: true; profile: 'local-character-budget'; limit: number;
  requests: { id: string; span: Span; text: string; sentenceIds: string[]; turnIds: string[]; voice: 'selected-loved-one' }[];
  blockers: string[];
}
export const points = (text: string): string[] => Array.from(text);
/** Textareas normalize CRLF/CR to LF and expose UTF-16 selection offsets. */
export function selectionSpan(text: string, start: number, end: number): Span {
  const a = points(text);
  const sourceOffset = (offset: number) => {
    let rendered = 0; let i = 0;
    while (i < a.length && rendered < offset) {
      if (a[i] === '\r' && a[i + 1] === '\n') { rendered++; i += 2; }
      else { rendered += a[i].length; i++; }
    }
    return i;
  };
  return [sourceOffset(start), sourceOffset(end)];
}
export const excerpt = (text: string, span: Span): string => points(text).slice(...span).join('');
const uid = (): string => {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
  const h = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  return `preview-${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
};
export async function digest(text: string): Promise<string> {
  const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(bytes), b => b.toString(16).padStart(2, '0')).join('');
}
const unit = (span: Span): Unit => ({ id: uid(), span, predecessors: [] });
const overlaps = (a: Span, b: Span) => a[0] < b[1] && b[0] < a[1];
const revise = (p: Preview, patch: Partial<Preview>): Preview => ({ ...p, ...patch, id: uid(), previousRevision: p.id });

/** Conservative quote recognition: apostrophes are never treated as turn boundaries. */
function quotedSpans(text: string): { spans: Span[]; warning: boolean } {
  const a = points(text); const spans: Span[] = [];
  let start = -1; let closer = ''; let warning = false;
  for (let i = 0; i < a.length; i++) {
    const c = a[i];
    if (start < 0 && (c === '“' || c === '"' || c === '«')) {
      start = i; closer = c === '“' ? '”' : c === '«' ? '»' : '"';
    } else if (start >= 0 && c === closer) {
      spans.push([start, i + 1]); start = -1;
    } else if (start >= 0 && c === '“') {
      // Continued multi-paragraph quotation. Keep the entire utterance together.
      warning = true;
    } else if (start < 0 && (c === '”' || c === '»')) warning = true;
  }
  if (start >= 0) { spans.push([start, a.length]); warning = true; }
  // Single-quote dialogue is ambiguous with apostrophes: surface, do not guess.
  if (/[‘’]/.test(text) || /(^|\s)'/.test(text)) warning = true;
  return { spans, warning };
}

function paragraphs(text: string): Paragraph[] {
  const a = points(text); const ends: number[] = [];
  const re = /(?:\r?\n)[\t ]*(?:\r?\n)(?:[\t ]*\r?\n)*/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) ends.push(points(text.slice(0, m.index + m[0].length)).length);
  if (ends[ends.length - 1] !== a.length) ends.push(a.length);
  let start = 0;
  return ends.filter(end => end > 0).map(end => {
    const span: Span = [start, end]; start = end;
    return { ...unit(span), kind: /\n/.test(excerpt(text, span).trim()) ? 'lineated' : 'paragraph' };
  });
}

function sentenceSpans(text: string, quotes: Span[], ps: Paragraph[]): Span[] {
  const a = points(text); const cuts: number[] = [];
  const insideQuote = (i: number) => quotes.some(([start, end]) => i >= start && i < end);
  for (let i = 0; i < a.length; i++) {
    if (!/[.!?。！？]/.test(a[i]) || insideQuote(i)) continue;
    const prefix = a.slice(Math.max(0, i - 16), i + 1).join('');
    if (a[i] === '.' && (
      /\b(?:Dr|Mr|Mrs|Ms|Prof|Sr|Jr|St|vs|etc)\.$/i.test(prefix) ||
      /\b[A-Z]\.$/.test(prefix) || /(?:\b[eE]\.g|\b[iI]\.e)\.$/.test(prefix) ||
      (/[0-9]/.test(a[i - 1] || '') && /[0-9]/.test(a[i + 1] || '')) ||
      a[i - 1] === '.' || a[i + 1] === '.'
    )) continue;
    if (i + 1 < a.length && !/\s/.test(a[i + 1])) continue;
    let end = i + 1;
    while (end < a.length && /\s/.test(a[end])) end++;
    cuts.push(end);
  }
  // Paragraphs are preferred boundaries unless within a continuing quotation.
  for (const p of ps) {
    const tail = excerpt(text, p.span).trim();
    if (!insideQuote(p.span[1] - 1) && /[.!?。！？][”’"»']?$/.test(tail)) cuts.push(p.span[1]);
  }
  cuts.push(a.length);
  let start = 0;
  return Array.from(new Set(cuts)).sort((a, b) => a - b).filter(end => end > 0).map(end => {
    const span: Span = [start, end]; start = end; return span;
  });
}

export async function prepare(input: Omit<Source, 'sha256' | 'mode'>): Promise<Preview> {
  if (!input.text.trim()) throw new Error('Choose an edition with text.');
  if (points(input.text).length > 100000) throw new Error('Preview supports up to 100,000 characters. Choose a shorter authored chapter.');
  const ps = paragraphs(input.text); const q = quotedSpans(input.text);
  const sentences = sentenceSpans(input.text, q.spans, ps).map(span => ({
    ...unit(span), paragraphIds: ps.filter(p => overlaps(p.span, span)).map(p => p.id),
  }));
  const turns: Turn[] = []; const characters: Character[] = [];
  let start = 0;
  const addTurn = (span: Span, role: Turn['role']) => {
    if (span[0] === span[1]) return;
    turns.push({ ...unit(span), role, characterId: null, state: 'unknown', candidates: [],
      evidence: role === 'dialogue' ? 'Quotation detected; speaker needs review.' : 'Narration in the selected loved one’s voice.' });
  };
  for (const span of q.spans) {
    addTurn([start, span[0]], 'narration'); addTurn(span, 'dialogue'); start = span[1];
    const after = excerpt(input.text, [span[1], Math.min(points(input.text).length, span[1] + 100)]);
    const match = after.match(/^\s*[,—]?\s*(?:([A-Z][a-z]+)\s+(?:said|asked|replied|whispered|explained)|(?:said|asked|replied|whispered|explained)\s+([A-Z][a-z]+))\b/);
    const name = match?.[1] || match?.[2];
    if (name) {
      let character = characters.find(c => c.name === name);
      if (!character) { character = { id: uid(), name, aliases: [] }; characters.push(character); }
      const turn = turns[turns.length - 1];
      turn.characterId = character.id; turn.candidates = [character.id]; turn.state = 'suggested';
      turn.evidence = `Possible speech tag: ${match![0].trim()}. Confirm the speaker.`;
    }
  }
  addTurn([start, points(input.text).length], 'narration');
  const passages: Passage[] = [];
  for (const sentence of sentences) {
    const last = passages[passages.length - 1];
    const previous = last && sentences.find(s => s.id === last.sentenceIds[last.sentenceIds.length - 1]);
    if (last && previous?.paragraphIds[0] === sentence.paragraphIds[0]) last.sentenceIds.push(sentence.id);
    else passages.push({ id: uid(), sentenceIds: [sentence.id], predecessors: [] });
  }
  return { schema: SCHEMA, id: uid(), previousRevision: null,
    source: { ...input, sha256: await digest(input.text), mode: 'exact-source' },
    paragraphs: ps, sentences, turns, characters, passages, boundariesReviewed: false,
    warnings: ['Sentence boundaries are conservative suggestions. Review them before accepting this preview.',
      ...(q.warning ? ['Quotation boundaries are ambiguous. Check nested, single and continued quotations.'] : []),
      ...(ps.some(p => p.kind === 'lineated') ? ['Line breaks are preserved; check poetry, headings and scene breaks manually.'] : [])] };
}

export function assignSpeaker(p: Preview, turnId: string, characterId: string | null): Preview {
  if (!p.turns.some(t => t.id === turnId && t.role === 'dialogue')) throw new Error('Choose a dialogue turn.');
  if (characterId && !p.characters.some(c => c.id === characterId)) throw new Error('Unknown character.');
  return revise(p, { turns: p.turns.map(t => t.id !== turnId ? t : { ...t, characterId,
    state: characterId ? 'human_confirmed' : 'human_unresolved', evidence: t.evidence }) });
}
export function addCharacter(p: Preview, name: string, aliases: string[] = []): Preview {
  const clean = name.trim(); const all = [clean, ...aliases.map(a => a.trim()).filter(Boolean)];
  if (!clean || all.some(n => n.length > 100)) throw new Error('Use a character name or alias of 1–100 characters.');
  const keys = all.map(n => n.toLocaleLowerCase());
  if (new Set(keys).size !== keys.length || p.characters.some(c => [c.name, ...c.aliases].some(n => keys.includes(n.toLocaleLowerCase())))) throw new Error('That name or alias already belongs to a character.');
  return revise(p, { characters: [...p.characters, { id: uid(), name: clean, aliases: all.slice(1) }] });
}
export function renameCharacter(p: Preview, id: string, name: string, aliases: string[]): Preview {
  if (!p.characters.some(c => c.id === id)) throw new Error('Unknown character.');
  const candidate = addCharacter({ ...p, characters: p.characters.filter(c => c.id !== id) }, name, aliases);
  const replacement = candidate.characters[candidate.characters.length - 1];
  return revise(p, { characters: p.characters.map(c => c.id === id ? { ...replacement, id } : c) });
}
export const reviewBoundaries = (p: Preview, reviewed: boolean): Preview => revise(p, { boundariesReviewed: reviewed });
export function mergePassage(p: Preview, index: number): Preview {
  const a = p.passages[index], b = p.passages[index + 1];
  if (!a || !b) throw new Error('Choose adjacent passages.');
  return revise(p, { passages: [...p.passages.slice(0, index), { id: uid(), sentenceIds: [...a.sentenceIds, ...b.sentenceIds], predecessors: [a.id, b.id] }, ...p.passages.slice(index + 2)] });
}
export function splitPassage(p: Preview, index: number, sentenceId: string): Preview {
  const a = p.passages[index]; const offset = a?.sentenceIds.indexOf(sentenceId) ?? -1;
  if (!a || offset < 1) throw new Error('Split before a later complete sentence.');
  return revise(p, { passages: [...p.passages.slice(0, index), ...[a.sentenceIds.slice(0, offset), a.sentenceIds.slice(offset)].map(sentenceIds => ({ id: uid(), sentenceIds, predecessors: [a.id] })), ...p.passages.slice(index + 1)] });
}
/** Correct a false sentence break without rewriting or moving source characters. */
export function joinSentence(p: Preview, index: number): Preview {
  const a = p.sentences[index], b = p.sentences[index + 1];
  if (!a || !b) throw new Error('Choose adjacent sentences.');
  const joined: Sentence = { id: uid(), span: [a.span[0], b.span[1]], predecessors: [a.id, b.id], paragraphIds: Array.from(new Set([...a.paragraphIds, ...b.paragraphIds])) };
  // A sentence cannot be divided across passages. Merge the affected passages first.
  const ai = p.passages.findIndex(x => x.sentenceIds.includes(a.id));
  const bi = p.passages.findIndex(x => x.sentenceIds.includes(b.id));
  const grouped = ai === bi ? p : mergePassage(p, ai);
  return revise(p, { boundariesReviewed: false, sentences: [...p.sentences.slice(0, index), joined, ...p.sentences.slice(index + 2)],
    passages: grouped.passages.map(x => x.sentenceIds.includes(a.id) || x.sentenceIds.includes(b.id) ? ({ ...x, id: uid(), predecessors: [x.id], sentenceIds: x.sentenceIds.flatMap(id => id === a.id ? [joined.id] : id === b.id ? [] : [id]) }) : x) });
}

export function validate(p: Preview, currentSource: Pick<Source, 'reference' | 'version' | 'text'> = p.source): string[] {
  const errors: string[] = []; const length = points(p.source.text).length;
  if (p.schema !== SCHEMA) errors.push('Unknown preview schema.');
  if (p.source.mode !== 'exact-source') errors.push('Only exact source wording is supported.');
  if (p.source.reference !== currentSource.reference || p.source.version !== currentSource.version || p.source.text !== currentSource.text) errors.push('Source revision changed. Start a new preview.');
  if (!length) errors.push('Source is empty.');
  const allIds = [...p.paragraphs, ...p.sentences, ...p.turns, ...p.characters, ...p.passages].map(x => x.id);
  if (new Set(allIds).size !== allIds.length || allIds.some(id => !id.startsWith('preview-'))) errors.push('Preview identities are invalid or duplicated.');
  const partition = (units: Unit[], label: string) => {
    let cursor = 0;
    for (const u of units) {
      const [start, end] = u.span;
      if (!Number.isInteger(start) || !Number.isInteger(end) || start !== cursor || end <= start || end > length) errors.push(`${label} spans must cover source exactly, without gaps or overlaps.`);
      cursor = end;
    }
    if (cursor !== length) errors.push(`${label} coverage is incomplete.`);
  };
  partition(p.paragraphs, 'Paragraph'); partition(p.sentences, 'Sentence'); partition(p.turns, 'Turn');
  const ordered = p.passages.flatMap(x => x.sentenceIds);
  if (p.passages.some(x => !x.sentenceIds.length) || JSON.stringify(ordered) !== JSON.stringify(p.sentences.map(s => s.id))) errors.push('Passages must cover complete sentences exactly once, in order.');
  for (const s of p.sentences) if (JSON.stringify(s.paragraphIds) !== JSON.stringify(p.paragraphs.filter(x => overlaps(x.span, s.span)).map(x => x.id))) errors.push('Sentence paragraph references are invalid.');
  if (!p.boundariesReviewed) errors.push('Review sentence and quotation boundaries.');
  for (const t of p.turns) {
    if (!['narration', 'dialogue'].includes(t.role) || !['unknown', 'suggested', 'human_confirmed', 'human_unresolved'].includes(t.state)) errors.push('Invalid turn role or attribution state.');
    if (t.candidates.some(id => !p.characters.some(c => c.id === id))) errors.push('Unknown speaker candidate.');
    if (t.characterId && !p.characters.some(c => c.id === t.characterId)) errors.push('Turn refers to an unknown character.');
    if (t.role === 'dialogue' && !['human_confirmed', 'human_unresolved'].includes(t.state)) errors.push('Confirm each suggested speaker or acknowledge uncertainty.');
    if (t.role === 'dialogue' && ((t.state === 'human_confirmed' && !t.characterId) || (t.state === 'human_unresolved' && t.characterId))) errors.push('Speaker review state is inconsistent.');
  }
  return Array.from(new Set(errors));
}

/** Local size simulation, NOT a provider contract or a narration fingerprint. */
export function plan(p: Preview, limit: number): RequestPlan {
  const result: RequestPlan = { previewOnly: true, profile: 'local-character-budget', limit, requests: [], blockers: validate(p) };
  if (!Number.isInteger(limit) || limit < 1 || limit > 100000) { result.blockers.push('Choose a character budget from 1 to 100,000.'); return result; }
  // Structural errors must never produce a plausible-looking partial plan.
  const reviewOnly = ['Review sentence and quotation boundaries.', 'Confirm each suggested speaker or acknowledge uncertainty.'];
  if (result.blockers.some(b => !reviewOnly.includes(b))) return result;
  const request = (sentences: Sentence[]) => {
    if (!sentences.length) return;
    const span: Span = [sentences[0].span[0], sentences[sentences.length - 1].span[1]];
    result.requests.push({ id: `preview-request-${result.requests.length + 1}`, span, text: excerpt(p.source.text, span), sentenceIds: sentences.map(s => s.id), turnIds: p.turns.filter(t => overlaps(t.span, span)).map(t => t.id), voice: 'selected-loved-one' });
  };
  for (const passage of p.passages) {
    let pending: Sentence[] = [];
    for (const id of passage.sentenceIds) {
      const s = p.sentences.find(x => x.id === id)!;
      if (s.span[1] - s.span[0] > limit) {
        result.blockers.push(`Sentence ${p.sentences.indexOf(s) + 1} exceeds this budget. Keep it intact and choose a larger budget or another provider profile later.`);
      }
      if (pending.length && s.span[1] - pending[0].span[0] > limit) { request(pending); pending = []; }
      pending.push(s);
    }
    request(pending);
  }
  if (result.blockers.some(x => x.includes('exceeds this budget'))) result.requests = [];
  return result;
}
export async function exportPreview(p: Preview, currentSource: Pick<Source, 'reference' | 'version' | 'text'>, limit: number): Promise<string> {
  const errors = validate(p, currentSource); const compiled = plan(p, limit);
  if (await digest(p.source.text) !== p.source.sha256) errors.push('Source digest does not match.');
  if (errors.length || compiled.blockers.length) throw new Error([...errors, ...compiled.blockers].join(' '));
  return JSON.stringify({ notice: 'Offline review snapshot only. Not approved content, a render request or a production identity.', preparation: p, simulation: compiled }, null, 2);
}

/** Human correction of a missed quotation, e.g. single quotes or script dialogue. */
export function markDialogue(p: Preview, span: Span): Preview {
  const [start, end] = span;
  const owners = p.turns.filter(t => overlaps(t.span, span));
  if (!owners.length || owners.some(t => t.role !== 'narration') || !Number.isInteger(start) || !Number.isInteger(end) || start < 0 || end > points(p.source.text).length || start >= end || !excerpt(p.source.text, span).trim()) throw new Error('Select speech within narration. Existing dialogue must be marked as narration before changing its boundaries.');
  const first = owners[0], last = owners[owners.length - 1];
  const make = (range: Span, role: Turn['role']): Turn => ({ ...unit(range), predecessors: owners.map(t => t.id), role, characterId: null, state: 'unknown', candidates: [], evidence: 'Boundary selected by the reviewer; speaker needs review.' });
  const replacements = [
    ...(start > first.span[0] ? [make([first.span[0], start], 'narration')] : []),
    make(span, 'dialogue'),
    ...(end < last.span[1] ? [make([end, last.span[1]], 'narration')] : []),
  ];
  const firstIndex = p.turns.indexOf(first), lastIndex = p.turns.indexOf(last);
  return revise(p, { boundariesReviewed: false, turns: [...p.turns.slice(0, firstIndex), ...replacements, ...p.turns.slice(lastIndex + 1)] });
}
export function markNarration(p: Preview, id: string): Preview {
  if (!p.turns.some(t => t.id === id)) throw new Error('Unknown turn.');
  return revise(p, { boundariesReviewed: false, turns: p.turns.map(t => t.id === id ? { ...t, id: uid(), predecessors: [t.id], role: 'narration', characterId: null, state: 'unknown', candidates: [], evidence: 'Reviewer marked this span as narration.' } : t) });
}
